'use client';

import type { PivotTable } from 'src/utils/pivot/pivot';
import type { ChartMode } from 'src/theme/chart-palette';
import type { AnalyticsMetric } from 'src/constants/pivot-fields';
import type { AnalyticsChartKind } from 'src/constants/pivot-charts';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { read, ALL_LABEL } from 'src/utils/pivot/pivot';
import { fNumber } from 'src/utils/format/format-number';
import { shortAxisDateLabel } from 'src/utils/format/chart-date-utils';
import { formatCompactNumber } from 'src/utils/format/format-compact-number';

import { RADIUS } from 'src/theme/ui-tokens';
import { CHART_OTHER, CHART_SERIES, isChartChannel, chartSeriesColor, chartChannelColor } from 'src/theme/chart-palette';

import { Chart, useChart, useChartMode } from 'src/components/chart';
import BrandChannelHeatmap from 'src/components/cmui/moments/content/charts/BrandChannelHeatmap';

/**
 * 피벗 한 장 → 그림 하나. 피벗 탭과 맞춤 대시보드가 **같은 부품**을 쓴다.
 *
 * 계열은 행, 가로축은 열이다. **열을 안 골랐으면 행이 가로축이 되고 계열은 하나다** —
 * 안 그러면 행마다 점 하나짜리 계열이 생겨 범례만 길어지고 막대가 한 칸에 겹친다.
 * 원형·트리맵만 행 합계 하나로 접어 그린다.
 * ⚠️ 열 축이 필요한 종류인데 열이 없으면 그리지 않고 **왜 못 그리는지** 적는다.
 * 🚨 높이는 **`options.chart.height`** 로 준다 — 래퍼가 그 값만 상류에 넘긴다(`components/chart`).
 *   prop 으로만 주면 바깥 div 의 속성으로 남고, 상류는 `100%` 를 받아 그림이 납작해진다.
 * ⛔ **A654** — 한 화면에 이 부품을 «둘 이상» 두면 뒤엣것이 빈 그래픽으로 남을 수 있다(계열 0).
 *   `chartId` 를 주면 상류가 그림마다 제 등록 열쇠를 갖는다 — **여러 장을 한 판에 둘 때는 꼭 준다.**
 * 🔑 **작게 그릴 때는 범례를 끈다**(`height` 가 `LEGEND_MIN` 미만). 후보 썸네일처럼 낮은
 *   자리에서는 계열이 여럿이면 범례가 그림을 통째로 덮는다(실측: 108px 가 전부 범례였다).
 *   (칸마다 따로 물어 그리는 맞춤 대시보드는 마운트 시점이 갈려 애초에 덜 겹친다)
 */

/** 차트에 그리는 계열 수 — 넘치면 표에서 본다(색이 스무 개를 넘으면 못 읽는다) */
const SERIES_MAX = 10;
/** 행이 **가로축**이 될 때의 상한 — 계열이 하나라 색이 겹치지 않으니 훨씬 많이 세울 수 있다 */
const CATEGORY_MAX = 90;
/** 상한에 잘려 나간 줄을 담는 조각의 이름 */
const REST_LABEL = '그 밖';
/** 트리맵에서 이름을 적는 칸의 최소 몫 — 이보다 작으면 한 글자도 안 들어간다(실측) */
const TREEMAP_LABEL_MIN_SHARE = 0.015;
/** 낱말 구름에 세우는 낱말 수 — 넘으면 글자가 다 작아져 무엇도 안 도드라진다 */
const CLOUD_MAX = 60;
const CLOUD_MIN_PX = 12;
const CLOUD_MAX_PX = 40;

/** 이 높이 미만이면 범례를 끈다 — 그 아래로는 범례가 그림을 덮는다(머리말) */
const LEGEND_MIN = 180;

export function AnalyticsChart({
  table,
  metric,
  kind,
  height = 320,
  legendTop = 0,
  legend = true,
  chartId,
}: {
  table: PivotTable;
  metric: AnalyticsMetric;
  kind: AnalyticsChartKind;
  height?: number;
  /**
   * 그림의 **등록 열쇠** — 한 판에 여러 장을 둘 때 준다(⛔ A654).
   * ⚠️ 판 안에서 유일해야 한다 — 겹치면 상류가 둘을 같은 그림으로 본다.
   */
  chartId?: string;
  /**
   * 도넛에 **이름표를 달고** 조각을 상위 N개로 줄인다(0이면 안 단다).
   * 🔑 조각 색만 있으면 무엇이 무엇인지 알 수 없다 — 이름표를 달 거면 조각도 줄여야 한다
   *   (열 조각짜리 이름표는 그림보다 길어진다). 남는 것은 늘 하던 대로 「그 밖」 한 조각이다.
   * ⚠️ 원형·트리맵(`totalsOnly`)에만 듣는다.
   */
  legendTop?: number;
  /** 그림 옆 범례 — `false` 면 부르는 쪽이 범례를 따로 그린다(채널 아이콘 범례 등) */
  legend?: boolean;
}) {
  const single = table.columns.length === 1 && table.columns[0] === ALL_LABEL;

  if (kind.needsColumn && single) {
    return (
      <Empty text={`${kind.label}은(는) 열 축이 있어야 그릴 수 있습니다 — 위에서 「열」을 골라 주세요.`} />
    );
  }
  if (table.rows.length === 0) {
    return <Empty text="그릴 값이 없습니다." />;
  }

  /*
    🔑 **순위 목록은 그림이 아니다** — 값이 큰 차례로 줄을 세운다. 계정·컨텐츠 유형처럼 이름이
      길고 가짓수가 많은 축은 막대로 그리면 이름이 잘려 무엇인지 못 읽는다.
  */
  if (kind.ranking) {
    return <RankingList table={table} metric={metric} height={height} />;
  }

  /* 🔑 낱말 구름도 그림이 아니다 — 크기가 값이다(순서는 뜻이 아니다) */
  if (kind.cloud) {
    return <WordCloud table={table} metric={metric} height={height} />;
  }

  /**
   * 행이 가로축이면(열 축 없음) 계열이 하나라 색이 안 겹친다 — 그때만 많이 세운다.
   * ⚠️ **가로 막대는 예외**다 — 칸이 세로로 쌓여서 아흔 줄이면 막대가 실선이 된다.
   */
  const flatAxis = single && !kind.totalsOnly;
  const rows = table.rows.slice(0, flatAxis && !kind.horizontal ? CATEGORY_MAX : SERIES_MAX);
  /**
   * 도움말에 적는 값 — **전체 수 그대로**다.
   * 🔑 축·도넛 한가운데는 짧게(`brief`) 적고 **정확한 수는 도움말에서 본다** — 두 자리의 일이 다르다.
   */
  const format = (value: number) => `${fNumber(Math.round(value))}${metric.unit ?? ''}`;
  /**
   * 축 눈금·도넛 한가운데에 적는 값 — **억·만으로 끊어** 짧게 적는다.
   * ⚠️ 한국어 화면이라 `ko` 자릿수다 — `1.2M` 은 「백이십만」으로 바로 안 읽힌다.
   * ⚠️ 단위 글자(`metric.unit`)는 안 붙인다 — 「4.5억건」처럼 겹쳐 읽히고 축이 좁다.
   */
  const brief = (value: number) =>
    formatCompactNumber(Math.round(value), { unit: 'ko', trimPointZero: true, nonPositive: 'zero' });

  if (kind.totalsOnly) {
    /**
     * 🚨 **잘려 나간 줄은 「그 밖」 한 조각으로 되살린다.**
     *   안 그러면 도넛이 상위 열 개만 더한 값을 「Total」이라 적어 머리 요약과 어긋난다
     *   (실측 메디큐브 30일: 채널이 열을 넘어 59,453 vs 59,533).
     * ⚠️ 표 전체의 합(`table.total`)에서 보이는 줄을 뺀 나머지다 — 줄을 다시 세지 않는다.
     */
    // 이름표를 달면 조각을 상위 N개로 줄인다 — 나머지는 아래에서 「그 밖」 한 조각이 된다
    const donutRows = legendTop > 0 ? rows.slice(0, legendTop) : rows;
    const shown = donutRows.map((row) => round(read(table.rowTotals[row], metric)));
    const rest = round(read(table.total, metric)) - shown.reduce((sum, value) => sum + value, 0);
    const labels = rest > 0 ? [...donutRows, REST_LABEL] : donutRows;
    const values = rest > 0 ? [...shown, rest] : shown;
    const whole = values.reduce((sum, value) => sum + value, 0);
    return (
      <ThemedChart
        type={kind.type}
        height={height}
        colorsFor={(mode) => rowColors(labels, mode)}
        series={kind.type === 'treemap' ? [{ data: labels.map((label, i) => ({ x: label, y: values[i] })) }] : values}
        options={{
          chart: { height, toolbar: { show: false }, ...(chartId ? { id: chartId } : {}) },
          labels,
          legend: (() => {
            /* 작게 그릴 때는 범례를 끈다 — 도넛·트리맵도 마찬가지다(머리말) */
            if (!legend || height < LEGEND_MIN) return { show: false };
            return legendTop > 0
              ? { show: true, position: 'right', horizontalAlign: 'left', fontSize: '12px', itemMargin: { horizontal: 6, vertical: 4 } }
              : { show: true, position: 'right' };
          })(),
          /**
           * 🚨 트리맵 이름표는 **`format: 'truncate'`** 다.
           *   기본값 `'scale'` 은 칸에 맞추려고 글자를 **눕히고 늘린다** — 「헤어/두피케어」가 90° 로 돌아갔다.
           *   `truncate` 는 늘 가로로 두고 넘치면 끝을 접는다.
           * ⚠️ 그래서 글자는 **작게** 둔다(11px) — 작은 칸도 이름이 들어가라고.
           * ⚠️ 그래도 이름 한 글자가 안 들어가는 칸은 접는다(`TREEMAP_LABEL_MIN_SHARE`) — 도움말로 본다.
           */
          ...(kind.type === 'treemap'
            ? { plotOptions: { treemap: { dataLabels: { format: 'truncate' } } } }
            // 도넛 한가운데 — 조각 값도 Total 도 억·만으로 끊어 적는다(정확한 수는 도움말에서 본다)
            : { plotOptions: { pie: { donut: { labels: { value: { formatter: brief }, total: { formatter: () => brief(whole) } } } } } }),
          dataLabels: kind.type === 'treemap'
            ? {
              enabled: true,
              style: { fontSize: '11px', fontWeight: 600 },
              formatter: (text: string, op: { value?: number }) =>
                (whole > 0 && (op?.value ?? 0) / whole >= TREEMAP_LABEL_MIN_SHARE ? text : ''),
            }
            : { enabled: false },
          tooltip: { y: { formatter: format } },
        }}
      />
    );
  }

  /*
    🔑 **히트맵은 이 부품이 안 그린다** — 대시보드가 이미 쓰는 격자 히트맵(`BrandChannelHeatmap`)
      하나로 모은다. 축만 갈아 끼운다.
    🚨 Apex 히트맵으로 그렸다가 걷었다 — 열을 카탈로그 수만큼 다 펴서 칸이 실선만큼 얇아지고
      이름표가 45° 로 겹쳤다(실측: 채널 × 브랜드 1행 × 40열이 막대 하나로 보였다). 격자 쪽은
      열 여덟까지만 펴고 나머지를 「기타」로 접으며, 색을 «줄 안에서» 정규화한다.
  */
  if (kind.type === 'heatmap') {
    return <GridHeatmap table={table} metric={metric} rows={rows} />;
  }

  /**
   * 🔑 **열 축이 없으면 행이 가로축이 된다.**
   * 열이 「전체」 하나뿐인데 행마다 계열을 만들면 계열 N개가 점 하나씩만 갖는 그림이 되고,
   * 날짜 행을 그렇게 그리면 **날짜가 범례에 늘어서고 막대가 한 칸에 겹친다**(읽을 수 없다).
   * 그래서 이때는 계열을 **하나**로 접고 행 이름을 가로축에 세운다.
   */
  const flat = flatAxis;
  const categories = flat ? rows : table.columns;

  /*
    🔑 **혼합은 계열마다 종류를 준다** — 첫 계열만 막대, 나머지는 선. 크기가 다른 두 이야기를
      한 판에 놓을 때 쓴다(막대가 바탕이고 선이 그 위를 지난다).
    ⚠️ 상류는 `chart.type` 이 `line` 일 때만 계열의 `type` 을 읽는다 — 그래서 종류가 `line` 이다.
  */
  const seriesType = (index: number) => (kind.mixed ? { type: index === 0 ? 'column' : 'line' } : {});

  return (
    <ThemedChart
      type={kind.type}
      height={height}
      colorsFor={flat ? undefined : (mode) => rowColors(rows, mode)}
      endDot={kind.type === 'line' || kind.type === 'area' ? categories.length - 1 : undefined}
      series={
        flat
          ? [{ name: metric.label, ...seriesType(0), data: rows.map((row) => round(read(table.rowTotals[row], metric))) }]
          : rows.map((row, i) => ({
            name: row,
            ...seriesType(i),
            data: table.columns.map((column) => round(read(table.cells[row]?.[column], metric))),
          }))
      }
      options={{
        chart: {
          height,
          toolbar: { show: false },
          stacked: Boolean(kind.stacked),
          ...(chartId ? { id: chartId } : {}),
        },
        plotOptions: { bar: { horizontal: Boolean(kind.horizontal), columnWidth: '60%' } },
        xaxis: { categories, ...axisLabels(categories.length) },
        // 선은 테마 기본(2px · 부드럽게 · 둥근 끝)을 그대로 쓴다
        /*
          ⚠️ **혼합은 두께를 계열마다 준다** — 하나로 주면 막대에도 테두리가 생긴다(0 이어야 한다).
        */
        ...(kind.mixed
          ? { stroke: { width: rows.map((_, i) => (i === 0 ? 0 : 2)) } }
          : {}),
        ...(kind.type === 'line' || kind.mixed ? {} : { stroke: { width: kind.type === 'area' ? 2 : 0 } }),
        // ⛔ `fill: undefined` 를 실으면 ApexCharts 가 기본 `fill` 을 지우고 `fill.colors` 를 읽다 터진다 — 값을 넣거나 아예 빼거나다
        // 그라데이션의 짙기는 테마가 정한다(옅은 물빛)
        /*
          ⚠️ 선은 채우기를 **단색으로 못 박는다** — 같은 그림이 영역 → 선으로 바뀌면(묶기 토글) ApexCharts 가
            앞의 `fill.type: gradient` 를 들고 가고, 선은 그 채우기로 획을 칠해 거의 투명해진다(끝점만 보인다).
        */
        ...(kind.type === 'area' ? { fill: { type: 'gradient' } } : kind.type === 'line' && !kind.mixed ? { fill: { type: 'solid', opacity: 1 } } : {}),
        dataLabels: { enabled: false },
        // 계열이 하나면 범례가 줄만 차지한다 · 작게 그릴 때는 아예 끈다(머리말)
        legend: { show: !flat && height >= LEGEND_MIN, position: 'top', horizontalAlign: 'left' },
        /**
         * ⚠️ **가로 막대에서는 세로축이 «이름»이다** — 여기에 숫자 표기를 걸면 `Math.round('클리오')` 가
         * NaN 이 되어 **축 이름이 통째로 빈칸으로 그려진다**(막대는 멀쩡해서 알아채기 어렵다).
         */
        yaxis: kind.horizontal ? {} : { labels: { formatter: brief } },
        tooltip: { y: { formatter: format } },
      }}
    />
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

/**
 * 테마를 태운 그림 한 장.
 *
 * 🚨 `Chart` 에 **생 옵션을 그대로 주면 테마가 통째로 빠진다** — 색이 ApexCharts 기본값
 *   (`#008ffb`)으로 떨어지고 글꼴·격자·범례도 우리 것이 아니게 된다(실측: 선 색이 테마 밖 파랑).
 *   `useChart` 가 기본값 위에 이 화면 옵션을 얹는다.
 * ⚠️ 훅이라 **갈래마다 이른 반환이 있는 바깥에서는 못 부른다** — 그래서 이 껍데기가 있다.
 * ⚠️ `seriesData` 는 넘기지 않는다 — 넘기면 `useChart` 가 `yaxis` 를 **제자리에서 고쳐** 축이 흔들린다.
 */
function ThemedChart({
  type,
  height,
  series,
  options,
  colorsFor,
  endDot,
}: {
  type: string;
  height: number;
  series: any[];
  options: any;
  /** 줄 이름으로 정한 색 — 없으면 테마의 범주 색 차례 */
  colorsFor?: (mode: ChartMode) => string[];
  /** 선의 **끝점**에 점을 찍을 칸 번호 — 지금 값이 어디인지 바로 보이게(선·영역만) */
  endDot?: number;
}) {
  const mode = useChartMode();
  const colors = colorsFor ? colorsFor(mode) : undefined;
  const palette = colors ?? CHART_SERIES[mode];
  const markers =
    endDot !== undefined && endDot >= 0
      ? {
        discrete: series.map((_, seriesIndex) => ({
          seriesIndex,
          dataPointIndex: endDot,
          size: 4,
          fillColor: palette[seriesIndex] ?? CHART_OTHER[mode],
          strokeColor: 'var(--palette-background-paper)',
        })),
      }
      : undefined;
  const merged = useChart({ ...options, ...(colors ? { colors } : {}), ...(markers ? { markers } : {}) });
  return <Chart type={type} height={height} series={series} options={merged} />;
}

/**
 * 줄(계열·조각)의 색.
 * 🔑 줄이 전부 채널이면 **채널 고정색**이다 — 같은 채널은 어느 화면에서나 같은 색이다.
 * 🔑 「그 밖」은 늘 회색이다 · 나머지는 범주 색을 차례로(돌려쓰지 않는다 — 넘치면 회색).
 */
function rowColors(labels: string[], mode: ChartMode): string[] {
  const named = labels.filter((label) => label !== REST_LABEL);
  const channels = named.length > 0 && named.every((label) => isChartChannel(label));
  return labels.map((label, i) => {
    if (label === REST_LABEL) return CHART_OTHER[mode];
    return channels ? chartChannelColor(label, mode) : chartSeriesColor(i, mode);
  });
}

/**
 * 가로축 라벨의 규칙.
 *
 * 🔑 **날짜 칸 이름은 언제나 짧게 적는다**(`shortAxisDateLabel`) — `YYYY-MM-DD` 열 자를 그대로
 *   주면 칸이 좁을 때 ApexCharts 가 해 네 자리에서 잘라 **전부 같은 글자가 된다**.
 * ⚠️ 빽빽할 때는 눕히지 않고(`rotate: 0`) 몇 개만 세운다(`tickAmount`) — 날짜 서른 개를 다 적으면
 *   45° 로 누워 그림 높이의 절반을 먹는다. 열 개 이하면 그냥 다 적는다.
 * ⚠️ 자르지 않는다(`trim: false`) — 자르면 짧게 적은 날짜도 칸 폭에 맞춰 「26…」만 남는다.
 *   겹치는 이름은 `hideOverlappingLabels` 가 걷는다.
 */
function axisLabels(count: number) {
  const labels = { formatter: shortAxisDateLabel };
  if (count <= 10) return { labels };
  return {
    tickAmount: 8,
    labels: { ...labels, rotate: 0, rotateAlways: false, hideOverlappingLabels: true, trim: false },
  };
}

function Empty({ text }: { text: string }) {
  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">{text}</Typography>
    </Box>
  );
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * **순위 목록** — 값이 큰 차례로 줄을 세운다.
 *
 * 🔑 막대 길이를 줄 안에 깔아 둔다 — 수만 적으면 1등과 2등의 차이가 눈에 안 들어온다.
 * ⚠️ 행 합계만 본다 — 열 축이 있어도 합쳐 센다(`totalsOnly` 와 같은 성질).
 * ⚠️ 높이를 넘기면 그 안에서 굴린다 — 대시보드의 다른 칸이 밀려 내려가면 한 판에 못 놓는다.
 */
function RankingList({ table, metric, height }: { table: PivotTable; metric: AnalyticsMetric; height: number }) {
  /* 🚨 `rowTotals` 는 **이름으로 찾는 표**다 — 번호로 읽으면 전부 `undefined` 라 늘 빈 목록이었다 */
  const rows = table.rows
    .map((label) => ({ label, value: read(table.rowTotals[label], metric) }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);
  if (rows.length === 0) return <Empty text="그릴 값이 없습니다." />;

  const top = rows[0].value || 1;
  return (
    <Box sx={{ height, overflowY: 'auto', pr: 0.5 }}>
      <Stack spacing={0.25}>
        {rows.map((row, i) => (
          <Box key={row.label} sx={{ position: 'relative', px: 1, py: 1, borderRadius: RADIUS.sm, overflow: 'hidden' }}>
            {/* 막대는 **글자 뒤에** 깔린다 — 위에 그리면 이름을 가린다 */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                width: `${Math.max(2, (row.value / top) * 100)}%`,
                bgcolor: 'primary.lighter',
                opacity: 0.55,
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ position: 'relative' }}>
              <Typography variant="uiMeta" color="text.disabled" sx={{ width: 20, flexShrink: 0 }}>
                {i + 1}
              </Typography>
              <Typography variant="uiBody" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {row.label}
              </Typography>
              <Typography variant="uiNum" sx={{ flexShrink: 0 }}>
                {fNumber(Math.round(row.value))}
                {metric.unit ?? ''}
              </Typography>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

/**
 * **낱말 구름** — 값이 큰 낱말을 크게 적는다.
 *
 * 🔑 크기가 값이다 — 순서는 뜻이 아니다(값 순으로 놓되 줄바꿈은 폭이 정한다).
 * ⚠️ 글자 크기는 **제곱근으로 눌러** 잡는다 — 값에 그대로 비례시키면 1등이 화면을 덮고 나머지가
 *   한 점이 된다(실측: 상위 하나가 전체의 절반인 표가 흔하다).
 * ⚠️ 행 합계만 본다 — 열 축이 있어도 합쳐 센다.
 */
function WordCloud({ table, metric, height }: { table: PivotTable; metric: AnalyticsMetric; height: number }) {
  const rows = table.rows
    .map((label) => ({ label, value: read(table.rowTotals[label], metric) }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, CLOUD_MAX);
  if (rows.length === 0) return <Empty text="그릴 값이 없습니다." />;

  const top = rows[0].value || 1;
  const size = (value: number) =>
    CLOUD_MIN_PX + (CLOUD_MAX_PX - CLOUD_MIN_PX) * Math.sqrt(Math.min(1, value / top));

  return (
    <Box sx={{ height, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start', gap: 1, p: 1 }}>
      {rows.map((row, i) => (
        <Box
          key={row.label}
          title={`${row.label} — ${fNumber(Math.round(row.value))}${metric.unit ?? ''}`}
          sx={{
            fontSize: `${size(row.value)}px`,
            lineHeight: 1.1,
            fontWeight: i < 3 ? 700 : 500,
            color: i < 3 ? 'primary.main' : 'text.secondary',
            cursor: 'default',
          }}
        >
          {row.label}
        </Box>
      ))}
    </Box>
  );
}

/**
 * **격자 히트맵** — 피벗 표를 대시보드의 히트맵 부품이 읽는 모양으로 옮긴다.
 *
 * 🔑 **그림은 그 부품이 그린다** — 열 접기(여덟 + 「기타」)·줄 안 정규화·도움말이 전부 거기 있다.
 * ⚠️ 축이 채널·국가가 아닐 수 있으므로 **열 이름을 그대로 넘긴다**(`columnLabel`) — 안 넘기면
 *   브랜드 이름을 채널 카탈로그에서 찾다가 못 찾고 열쇠가 그대로 뜬다.
 * ⚠️ 제목과 「채널/국가」 칩은 끈다 — 칸의 머리는 맞춤 대시보드 카드가 단다.
 */
function GridHeatmap({
  table,
  metric,
  rows,
}: {
  table: PivotTable;
  metric: AnalyticsMetric;
  rows: string[];
}) {
  const data = rows.map((row) => ({
    name: row,
    total: read(table.rowTotals[row], metric),
    bySource: Object.fromEntries(
      table.columns.map((column) => [column, read(table.cells[row]?.[column], metric)]),
    ),
  }));

  return (
    <BrandChannelHeatmap
      rows={data}
      mode="platform"
      maxRows={rows.length}
      title=""
      showModeChips={false}
      columnLabel={(key) => key}
      emptyText="그릴 값이 없습니다."
    />
  );
}
